import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiConfiguration } from '../api/api-configuration';
import { AuthService } from '../../auth/auth.service';
import { OfflineDataService } from './offline-data.service';
import { AlertService } from '../../shared/services/alert.service';
import { AppVersionService } from './app-version.service';
import { FrontendDiagnosticsService, getSparkClientContext } from './frontend-diagnostics.service';

export interface SendLogsPayload {
  appVersion?: string;
  context?: string;
  loggedInUser?: object;
  frontendErrors?: string[];
  offlineUnsyncedDataJson?: string;
}

@Injectable({ providedIn: 'root' })
export class SendLogsService {
  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfiguration,
    private authService: AuthService,
    private offlineDataService: OfflineDataService,
    private alertService: AlertService,
    private appVersionService: AppVersionService,
    private frontendDiagnosticsService: FrontendDiagnosticsService
  ) {}

  /** Record a frontend error for inclusion in the next Send logs. */
  addError(message: string, stack?: string): void {
    this.frontendDiagnosticsService.addError(message, stack);
  }

  /** Record a frontend diagnostic breadcrumb for investigation. */
  addDiagnostic(message: string, context?: string): void {
    this.frontendDiagnosticsService.addDiagnostic(message, context);
  }

  /** Get recent errors (read-only). */
  getRecentErrors(): string[] {
    return this.frontendDiagnosticsService.getRecentEntries();
  }

  /**
   * Build and send log payload to backend (offline unsynced data, user info, frontend errors, context).
   * Uses existing Opto logging (LogUserAudit) on the server.
   */
  sendLogs(context?: string): void {
    this.sendLogsAsync(context).then(() => undefined);
  }

  sendLogsAsync(
    context?: string,
    options?: { showAlert?: boolean }
  ): Promise<{ ok: boolean; reason?: string }> {
    const showAlert = options?.showAlert !== false;
    const baseUrl =
      this.apiConfig.rootUrl || (this.apiConfig as any).webApiRoot || '';
    const url = `${baseUrl}/api/Account/SendLogs`;

    const appVersion = this.appVersionService.getVersionRaw();
    const payload: SendLogsPayload = {
      appVersion: appVersion ?? undefined,
      context: JSON.stringify(getSparkClientContext()) + ' | ' + (context || 'User clicked Send logs')
        + '; updateStatus=' + this.appVersionService.status
        + '; availableRelease=' + (this.appVersionService.getLatestVersionRaw() || 'unknown'),
      loggedInUser: this.authService.userInfo
        ? {
            ID: this.authService.userInfo.ID,
            UserName: this.authService.userInfo.UserName,
            Email: this.authService.userInfo.Email,
            Fullnane: this.authService.userInfo.Fullnane,
          }
        : undefined,
      frontendErrors:
        this.getRecentErrors().length > 0 ? this.getRecentErrors() : undefined,
      offlineUnsyncedDataJson:
        this.offlineDataService.getUnsyncedOfflineDataJson(),
    };

    return new Promise((resolve) => {
      this.http.post(url, payload).subscribe({
        next: () => {
          if (showAlert) {
            this.alertService.success('Logs sent successfully');
          }
          resolve({ ok: true });
        },
        error: (err) => {
          console.error('Send logs failed', err);
          if (showAlert) {
            this.alertService.error('Failed to send logs. You may be offline.');
          }
          resolve({
            ok: false,
            reason: err?.message || 'Failed to send logs',
          });
        },
      });
    });
  }
}
